'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useSearchSuggestions } from '@/lib/queries';
import type { SearchSuggestion } from '@/types';

interface TopBarSearchProps {
  isTransparent?: boolean;
}

export default function TopBarSearch({ isTransparent = false }: TopBarSearchProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const router = useRouter();
  const { data: suggestionsData } = useSearchSuggestions(debouncedSearchTerm);

  const suggestions = suggestionsData?.suggestions || [];

  // Reset active index when suggestions change
  useEffect(() => {
    setActiveIndex(-1);
  }, [suggestions]);

  // Scroll active suggestion into view
  useEffect(() => {
    if (activeIndex >= 0 && itemRefs.current[activeIndex]) {
      itemRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setShowSuggestions(searchTerm.length >= 2);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedContainer = containerRef.current?.contains(event.target as Node);
      const clickedSuggestions = suggestionsRef.current?.contains(event.target as Node);
      if (!clickedContainer && !clickedSuggestions) {
        setIsOpen(false);
        setSearchTerm('');
        setShowSuggestions(false);
      }
    };
    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, []);

  // Close on ESC
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setSearchTerm('');
        setShowSuggestions(false);
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen]);

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    setShowSuggestions(false);
    setIsOpen(false);
    setSearchTerm('');

    if (suggestion.type === 'castle') {
      router.push(`/castle/${suggestion.slug}/${suggestion.id}`);
    } else if (suggestion.type === 'country') {
      router.push(`/country/${suggestion.slug}`);
    } else if (suggestion.type === 'region') {
      if (suggestion.countryId) {
        router.push(`/castles?countryId=${suggestion.countryId}&regionId=${suggestion.id}`);
      }
    } else if (suggestion.type === 'city') {
      router.push(`/castles?city=${encodeURIComponent(suggestion.name)}`);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      setShowSuggestions(false);
      setIsOpen(false);
      router.push(`/castles?search=${encodeURIComponent(searchTerm.trim())}`);
      setSearchTerm('');
    }
  };

  const iconColor = isTransparent ? 'text-white' : 'text-gray-700 dark:text-gray-200';
  const hoverBg = isTransparent ? 'hover:bg-white/10' : 'hover:bg-gray-100 dark:hover:bg-gray-800';

  const getSuggestionIcon = (type: string) => {
    switch (type) {
      case 'castle':
        return (
          <svg
            className="w-4 h-4 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
        );
      case 'country':
        return (
          <svg
            className="w-4 h-4 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"
            />
          </svg>
        );
      case 'region':
        return (
          <svg
            className="w-4 h-4 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
            />
          </svg>
        );
      case 'city':
        return (
          <svg
            className="w-4 h-4 text-purple-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div ref={containerRef} className="relative flex items-center">
      {/* Search Icon Button */}
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          if (isOpen) {
            setSearchTerm('');
            setShowSuggestions(false);
          }
        }}
        className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 ${iconColor} ${hoverBg}`}
        aria-label={isOpen ? 'Close search' : 'Open search'}
      >
        {isOpen ? (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        )}
      </button>

      {/* Search Input Panel */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50 dark:bg-gray-800 dark:border-gray-700">
          <form onSubmit={handleSubmit} className="relative">
            <input
              ref={inputRef}
              type="text"
              placeholder="Search castles, countries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => searchTerm.length >= 2 && setShowSuggestions(true)}
              onKeyDown={(e) => {
                if (!showSuggestions || suggestions.length === 0) return;

                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setActiveIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
                } else if (e.key === 'Enter' && activeIndex >= 0) {
                  e.preventDefault();
                  handleSuggestionClick(suggestions[activeIndex]);
                }
              }}
              className="w-full px-4 py-3 pl-11 text-gray-900 placeholder-gray-500 bg-white border-0 focus:ring-0 focus:outline-none dark:text-gray-100 dark:placeholder-gray-400 dark:bg-gray-800"
            />
            <svg
              className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </form>

          {/* Suggestions Dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div
              ref={suggestionsRef}
              className="border-t border-gray-100 max-h-72 overflow-y-auto dark:border-gray-700"
            >
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  ref={(el) => {
                    itemRefs.current[index] = el;
                  }}
                  onClick={() => handleSuggestionClick(suggestion)}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={`w-full px-4 py-2.5 text-left flex items-center gap-3 border-b border-gray-50 last:border-b-0 transition-colors dark:border-gray-800 ${
                    index === activeIndex
                      ? 'bg-gray-100 dark:bg-gray-700'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="flex-shrink-0">{getSuggestionIcon(suggestion.type)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 text-sm truncate dark:text-gray-100">
                      {suggestion.name}
                    </div>
                    {suggestion.location && (
                      <div className="text-xs text-gray-500 truncate dark:text-gray-400">
                        {suggestion.location}
                      </div>
                    )}
                  </div>
                  <span
                    className={`flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded font-medium ${
                      suggestion.type === 'castle'
                        ? 'bg-red-100 text-red-700'
                        : suggestion.type === 'country'
                          ? 'bg-blue-100 text-blue-700'
                          : suggestion.type === 'region'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-purple-100 text-purple-700'
                    }`}
                  >
                    {suggestion.type}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Empty state */}
          {showSuggestions && searchTerm.length >= 2 && suggestions.length === 0 && (
            <div className="px-4 py-6 text-center border-t border-gray-100 dark:border-gray-700">
              <p className="text-sm text-gray-500 dark:text-gray-400">No results found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
